import type { ReactNode } from "react";
import { Info, Trash2, Zap } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";

/** Pozycja automatyczna umowy ramowej — kształt zgodny z LineItemTypeSerializer
 *  (+ werdykt z GET /orders/auto-line-items/). */
export interface AutoLineItemInfo {
  id: string;
  name: string;
  unit: string;
  base_rate: string | null;
  high_priority_rate?: string | null;
  attachment_required: boolean;
  /** Pozycje wyzwalające — którakolwiek z nich na zamówieniu dopisuje tę pozycję (raz). */
  auto_triggers: string[];
  auto_trigger_names: string[];
  /** Nazwy wyzwalaczy sklejone przecinkiem (zgodność ze starszymi konsumentami). */
  auto_trigger_name?: string | null;
  auto_quantity: string | null;
  auto_power_projects_only: boolean;
  auto_once_per_project: boolean;
  auto_pricing_mode: "rate" | "lump_sum" | "time_based";
  auto_time_tiers: { days: number; amount: string }[];
  applicable?: boolean;
  skip_reason?: string;
}

export const POWER_ONLY_TIP =
  "Pozycja dopisuje się tylko do zamówień w projektach energetycznych (zlecanych z modułu DLC01).";
export const ONCE_PER_PROJECT_TIP =
  "Pozycja dopisuje się tylko do pierwszego zamówienia w danym przedsięwzięciu; kolejne " +
  "zamówienia z pozycją wyzwalającą już jej nie dostają.";
export const PRICING_TIPS: Record<AutoLineItemInfo["auto_pricing_mode"], string> = {
  rate: "Liczona jak w cenniku: ilość × stawka.",
  lump_sum: "Jedna kwota (premia) — na zamówieniu raz, z pełną kwotą.",
  time_based:
    "Kwota zależy od liczby dni od przyjęcia zamówienia do zgłoszenia wykonania: obowiązuje " +
    "kwota pierwszego nieprzekroczonego progu, po ostatnim progu 0 zł. Na zamówieniu startuje " +
    "z kwotą pierwszego progu i jest przeliczana raz, przy zgłoszeniu wykonania.",
};

/** „A”, „B” — nazwy pozycji wyzwalających do etykiet („dodawana przy …”). */
export function autoTriggerLabel(item: { auto_trigger_names?: string[] | null; auto_trigger_name?: string | null }): string {
  const names = item.auto_trigger_names?.length
    ? item.auto_trigger_names
    : item.auto_trigger_name
      ? [item.auto_trigger_name]
      : [];
  return names.length ? names.map((n) => `„${n}”`).join(", ") : "„?”";
}

/** Ilość wiersza automatycznego w formularzu: stała / jak w wyzwalaczu / 1 dla kwot. */
export function autoRowQuantity(item: AutoLineItemInfo, triggerQuantity: number | string | undefined): number {
  if (item.auto_pricing_mode !== "rate") return 1;
  if (item.auto_quantity != null && item.auto_quantity !== "") return parseFloat(item.auto_quantity) || 1;
  const q = Number(triggerQuantity);
  return q > 0 ? q : 1;
}

function pricingLabel(item: AutoLineItemInfo): string {
  if (item.auto_pricing_mode === "time_based") {
    return (
      (item.auto_time_tiers ?? []).map((t) => `do ${t.days} dni: ${formatAmount(t.amount)} zł`).join(" · ") +
      " · potem 0 zł"
    );
  }
  if (item.auto_pricing_mode === "lump_sum") return `pełna kwota: ${formatAmount(item.base_rate ?? "0")} zł`;
  return `${item.unit} @ ${item.base_rate != null ? formatAmount(item.base_rate) : "—"} zł`;
}

/** Wiersz pozycji automatycznej w formularzu zapotrzebowania — wartości tylko do odczytu
 *  (siedzą w formularzu, useFieldArray), a tu jest to, co pokazuje tabela w umowie ramowej.
 *  `onRemove` = kosz (wiersz ręczny: usuń; wiersz przy wyzwalaczu: pomiń pozycję). */
export function AutoLineItemRow({
  item,
  quantity,
  manual = false,
  quantityInput,
  onRemove,
  removeTitle = "Usuń pozycję",
}: {
  item: AutoLineItemInfo;
  quantity: number | string;
  /** Wskazana ręcznie z listy „Z umowy” (a nie dopisana przy wyzwalaczu). */
  manual?: boolean;
  /** Edytowalne pole ilości (wiersz ręczny w trybie ilość × stawka). */
  quantityInput?: ReactNode;
  onRemove?: () => void;
  removeTitle?: string;
}) {
  const modeLabel = { rate: "ilość × stawka", lump_sum: "pełna kwota", time_based: "zmienna w czasie" }[
    item.auto_pricing_mode
  ];
  return (
    <div
      className="ml-6 border-l-2 border-sky-300 bg-sky-50/60 p-3 text-xs dark:border-sky-800 dark:bg-sky-900/10"
      data-testid="auto-line-item-row"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
          <Zap size={10} /> pozycja automatyczna
        </span>
        <span className="font-medium text-gray-900 dark:text-gray-100">{item.name}</span>
        <span className="text-gray-500 dark:text-gray-400">
          {manual ? "wskazana ręcznie · " : ""}dodawana przy {autoTriggerLabel(item)}
        </span>
        {manual && !item.applicable && item.skip_reason && (
          <span className="text-amber-700" title={item.skip_reason}>
            (sama by się nie dodała: {item.skip_reason})
          </span>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
            title={removeTitle}
            aria-label={removeTitle}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
        <div>
          <span className="block text-[10px] uppercase tracking-wide text-gray-400">Wycena</span>
          <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300" title={PRICING_TIPS[item.auto_pricing_mode]}>
            {modeLabel} <Info size={11} className="text-gray-400" />
          </span>
          <span className="block text-gray-600 dark:text-gray-300">{pricingLabel(item)}</span>
        </div>
        <div>
          <span className="block text-[10px] uppercase tracking-wide text-gray-400">Ilość</span>
          {quantityInput ?? (
            <span className="text-gray-700 dark:text-gray-300">
              {formatAmount(String(quantity))}
              {item.auto_pricing_mode === "rate" && (item.auto_quantity == null || item.auto_quantity === "") && (
                <span className="text-gray-400"> (jak w pozycji wyzwalającej)</span>
              )}
            </span>
          )}
        </div>
        <div className="col-span-2">
          <span className="block text-[10px] uppercase tracking-wide text-gray-400">Zakres</span>
          <span className="inline-flex flex-wrap gap-1">
            <span
              className="rounded bg-white px-1.5 py-0.5 text-[10px] text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700"
              title={item.auto_power_projects_only ? POWER_ONLY_TIP : "Dopisuje się w każdym projekcie."}
            >
              {item.auto_power_projects_only ? "tylko projekty energetyczne" : "każdy projekt"}
            </span>
            <span
              className="rounded bg-white px-1.5 py-0.5 text-[10px] text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700"
              title={item.auto_once_per_project ? ONCE_PER_PROJECT_TIP : "Dopisuje się do każdego zamówienia z pozycją wyzwalającą."}
            >
              {item.auto_once_per_project ? "raz na przedsięwzięcie" : "każde zamówienie"}
            </span>
            {item.attachment_required && (
              <span className="rounded bg-white px-1.5 py-0.5 text-[10px] text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
                załącznik przy POCK
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
